import { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Updater, useImmer } from "use-immer";
import {
  Box,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { LightModeOutlined, DarkModeOutlined } from "@mui/icons-material";
import { useAppDispatch } from "@store/hooks";
import { changeMode } from "@store/slices/themeSlice";

interface Props {
  modals: (
    modalVisible: string,
    setModalVisible: Updater<string>
  ) => ReactNode[];
}

/**
 * Landing-page footer. Two groups separated on sm+, stacked on xs:
 *
 *   [ © year + Terms ]                    [ Docs · Contact · theme ]
 *
 * Docs and Contact live here (and only here on logged-out surfaces).
 * The theme toggle stays at the right — subtle utility space, same
 * convention used by Notion / Linear / Stripe.
 */
export default function Footer({ modals }: Props) {
  const [modalVisible, setModalVisible] = useImmer("");
  const navigate = useNavigate();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const year = new Date().getFullYear();
  const isDark = theme.palette.mode === "dark";

  return (
    <Box
      component="footer"
      sx={{
        // Semantic <footer>, not <AppBar> — AppBar carries chrome that
        // reads as a second navigation rather than a quiet boundary.
        bgcolor: "background.paper",
        borderTop: 1,
        borderColor: "divider",
        py: { xs: 1.5, sm: 1.25 },
        px: { xs: 2, sm: 3 },
        mt: "auto",
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={{ xs: 1.5, sm: 2 }}
        alignItems={{ xs: "center", sm: "center" }}
        justifyContent="space-between"
        sx={{ rowGap: 1 }}
      >
        <Stack
          direction="row"
          spacing={{ xs: 1.5, sm: 3 }}
          alignItems="center"
          flexWrap="wrap"
          justifyContent="center"
          sx={{ rowGap: 0.5 }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ flexShrink: 0 }}
          >
            © {year} AnnoGram
          </Typography>
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={() => navigate("/terms")}
          >
            Terms
          </Link>
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={() => navigate("/privacy")}
          >
            Privacy
          </Link>
        </Stack>

        <Stack
          direction="row"
          spacing={{ xs: 1.5, sm: 2.5 }}
          alignItems="center"
          flexWrap="wrap"
          justifyContent="center"
          sx={{ rowGap: 0.5 }}
        >
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={() => setModalVisible("contact")}
          >
            Contact
          </Link>
          <Tooltip title={isDark ? "Switch to light theme" : "Switch to dark theme"}>
            <IconButton
              size="small"
              onClick={() => dispatch(changeMode())}
              // The icon shape (sun vs moon) carries the semantic
              // load. Color comes from the theme so neither mode has
              // a contrast fail (the previous hardcoded #FFFF9F on
              // light failed WCAG against the paper background).
              sx={{ color: "text.primary" }}
              aria-label={
                isDark ? "Switch to light theme" : "Switch to dark theme"
              }
            >
              {isDark ? (
                <DarkModeOutlined fontSize="small" />
              ) : (
                <LightModeOutlined fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
      {modals(modalVisible, setModalVisible)}
    </Box>
  );
}
